import React from 'react';
import {
    CommandBar,
    IColumn,
    ICommandBarItemProps,
    Image,
    ImageFit,
    MessageBar,
    MessageBarType,
    SelectionMode,
    ShimmeredDetailsList,
    useTheme,
} from '@fluentui/react';
import { toast } from 'react-toastify';
import { IStorefrontAsset, STOREFRONT_ASSET_GLOBAL } from '../../../models/Storefront';
import {
    useGetStorefrontAssetsQuery,
    useUploadStorefrontAssetMutation,
} from '../../../store/endpoints/StorefrontAssetEndpoints';
import { useGetStorefrontsQuery } from '../../../store/endpoints/StorefrontSettingsEndpoints';
import { ColumnGenerator } from '../../../logic/Columns/ColumnGenerator';
import AssetDetailsPanel from './AssetDetailsPanel';
import ScanAssetsDialog from './ScanAssetsDialog';
import { assetSourceLabel, extractErrorMessage, formatBytes } from './assetFormatting';

// The management surface for the storefront image library: every registered asset across the org's
// storefronts plus its globals. Uploads made here default to the `global` scope; an asset uploaded
// from the library screen isn't tied to any one storefront.
const AssetsTable: React.FC = () => {
    const theme = useTheme();
    const assets = useGetStorefrontAssetsQuery(undefined);
    const storefronts = useGetStorefrontsQuery();
    const [uploadAsset] = useUploadStorefrontAssetMutation();

    const [selectedId, setSelectedId] = React.useState<string | undefined>();
    // Holds a just-uploaded asset until the invalidated list comes back with it.
    const [pendingAsset, setPendingAsset] = React.useState<IStorefrontAsset | undefined>();
    const [panelOpen, setPanelOpen] = React.useState(false);
    const [scanOpen, setScanOpen] = React.useState(false);
    const [uploading, setUploading] = React.useState(false);
    const [errorMessage, setErrorMessage] = React.useState<string | undefined>();

    const uploadInputRef = React.useRef<HTMLInputElement | null>(null);

    const items = React.useMemo(() => assets.data?.items ?? [], [assets.data]);
    // Read the selection back out of the list so renames/promotes/replaces made in the panel are
    // reflected without re-plumbing the object.
    const selected = React.useMemo(
        () => items.find(a => a.id === selectedId) ?? (pendingAsset?.id === selectedId ? pendingAsset : undefined),
        [items, selectedId, pendingAsset]
    );

    const storefrontNames = React.useMemo(() => {
        const map: { [id: string]: string } = {};
        (storefronts.data?.items ?? []).forEach(s => {
            if (s.id) map[s.id] = s.name ?? s.id;
        });
        return map;
    }, [storefronts.data]);

    const scopeLabel = (asset: IStorefrontAsset) => asset.storefront === STOREFRONT_ASSET_GLOBAL
        ? 'Global'
        : (storefrontNames[asset.storefront] ?? asset.storefront);

    const openAsset = (asset: IStorefrontAsset) => {
        setSelectedId(asset.id);
        setPanelOpen(true);
    };

    const onUploadFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        // Allow re-picking the same file after a failure.
        e.target.value = '';
        if (!file) return;
        if (file.size > 20971520) {
            setErrorMessage('File size exceeds 20 MB');
            return;
        }
        setErrorMessage(undefined);
        setUploading(true);
        const objectName = file.name;
        const toastId = toast.loading(`Uploading ${objectName}…`);
        try {
            const buffer = await file.arrayBuffer();
            const response = await uploadAsset({
                file: buffer,
                fileName: file.name,
                storefront: STOREFRONT_ASSET_GLOBAL,
                name: file.name.replace(/\.[^./\\]+$/, ''),
            }).unwrap();
            toast.update(toastId, { type: 'success', render: `${objectName} uploaded`, isLoading: false, autoClose: 3000 });
            if (response.asset) {
                // Straight into the details panel so the new asset can be named/tagged.
                setPendingAsset(response.asset);
                setSelectedId(response.asset.id);
                setPanelOpen(true);
            }
        } catch (err) {
            const detail = extractErrorMessage(err);
            setErrorMessage(detail ?? 'Failed to upload asset');
            toast.update(toastId, {
                type: 'error',
                render: `Failed to upload ${objectName}${detail ? `: ${detail}` : ''}`,
                isLoading: false,
                autoClose: 4000,
            });
        }
        setUploading(false);
    };

    const columns: IColumn[] = [
        {
            key: 'thumbnail',
            name: '',
            minWidth: 56,
            maxWidth: 56,
            onRender: (item: IStorefrontAsset) => (
                <Image src={item.url} alt={item.name} width={48} height={32} imageFit={ImageFit.contain} />
            ),
        },
        ColumnGenerator('Name', 'name', 140, 260),
        ColumnGenerator('Tags', 'tags', 120, 220, (item: IStorefrontAsset) => (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {(item.tags ?? []).map(tag => (
                    <span
                        key={tag}
                        style={{
                            fontSize: 11,
                            padding: '1px 6px',
                            borderRadius: 10,
                            backgroundColor: theme.palette.themeLighter,
                            color: theme.palette.themeDarker,
                        }}
                    >
                        {tag}
                    </span>
                ))}
            </div>
        ), true),
        ColumnGenerator('Scope', 'storefront', 120, 180, (item: IStorefrontAsset) => <div>{scopeLabel(item)}</div>),
        ColumnGenerator('Size', 'sizeBytes', 80, 90, (item: IStorefrontAsset) => (
            <div style={{ textAlign: 'right' }}>{formatBytes(item.sizeBytes)}</div>
        )),
        ColumnGenerator('Source', 'source', 100, 130, (item: IStorefrontAsset) => (
            <div>{assetSourceLabel(item.source)}</div>
        )),
        ColumnGenerator('Last Updated', 'lastUpdated', 140, 180, (item: IStorefrontAsset) => (
            <div>{item.lastUpdated ? new Date(item.lastUpdated).toLocaleString() : ''}</div>
        )),
    ];

    const commands: ICommandBarItemProps[] = [
        {
            key: 'scan',
            text: 'Scan for Assets',
            iconProps: { iconName: 'SearchAndApps' },
            onClick: () => setScanOpen(true),
        },
        {
            key: 'upload',
            text: 'Upload Asset',
            iconProps: { iconName: 'Upload' },
            disabled: uploading,
            onClick: () => uploadInputRef.current?.click(),
        },
    ];

    const farCommands: ICommandBarItemProps[] = [
        {
            key: 'refresh',
            text: 'Refresh',
            iconProps: { iconName: 'Refresh' },
            iconOnly: true,
            onClick: () => { assets.refetch() },
        },
    ];

    return (
        <div>
            <input
                type='file'
                ref={uploadInputRef}
                style={{ display: 'none' }}
                onChange={onUploadFile}
                accept='.gif,.png,.jpg,.jpeg,.webp,.svg,image/svg+xml'
            />
            <CommandBar items={commands} farItems={farCommands} />
            {errorMessage && (
                <MessageBar messageBarType={MessageBarType.error} onDismiss={() => setErrorMessage(undefined)}>
                    {errorMessage}
                </MessageBar>
            )}
            {assets.isError && (
                <MessageBar messageBarType={MessageBarType.error}>Failed to load assets.</MessageBar>
            )}
            <ShimmeredDetailsList
                enableShimmer={assets.isLoading}
                items={items}
                columns={columns}
                selectionMode={SelectionMode.none}
                onItemInvoked={(item?: IStorefrontAsset) => { if (item) openAsset(item) }}
                onRenderRow={(rowProps, defaultRender) => {
                    // A real click, not onActiveItemChanged: that fires on row focus, so the
                    // panel's dismiss (which returns focus to the row) would reopen it.
                    if (!rowProps || !defaultRender) return null;
                    return (
                        <div style={{ cursor: 'pointer' }} onClick={() => openAsset(rowProps.item)}>
                            {defaultRender(rowProps)}
                        </div>
                    );
                }}
            />
            <AssetDetailsPanel
                isOpen={panelOpen}
                onDismiss={() => setPanelOpen(false)}
                asset={selected}
                storefrontName={selected ? storefrontNames[selected.storefront] : undefined}
            />
            <ScanAssetsDialog isOpen={scanOpen} onDismiss={() => setScanOpen(false)} />
        </div>
    );
};

export default AssetsTable;
